/**
 * Audio Effects Manager for Immersive Bible Reading — Lumera
 *
 * Two modes:
 *   1. Auto-detect: analyse verse text and play matching ambient layers
 *   2. Manual scene: user picks a curated soundscape (wilderness, seaside, ...)
 *
 * Uses the Web Audio API with procedurally generated noise buffers so that
 * no external audio asset is required. Effects are gently looped and
 * crossfaded for a smooth, cinematic feel while narration plays.
 */

class AudioEffectsManager {
  constructor() {
    this.audioContext = null;
    this.masterGain = null;
    this.effectsEnabled = true;
    this.effectVolume = 0.3; // 0..1 master bus
    this.useRealSamples = false; // premium flag
    /** @type {Map<string, {sources: AudioNode[], gain: GainNode, stopAt?: number}>} */
    this.activeEffects = new Map();
    /** @type {Map<string, AudioBuffer>} Decoded real-sample buffers, by layer name */
    this.sampleBuffers = new Map();
    /** @type {Set<string>} Names we've tried and failed to load — skip next time */
    this.sampleMissing = new Set();

    // Cached noise buffer (generated once, reused everywhere)
    this._noiseBuffer = null;

    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) {
        this.audioContext = new Ctx();
        this.masterGain = this.audioContext.createGain();
        this.masterGain.gain.value = this.effectVolume;
        this.masterGain.connect(this.audioContext.destination);
      }
    } catch (e) {
      console.warn('[AudioEffects] Web Audio API unavailable:', e);
    }
  }

  /** Resume/activate the AudioContext from a user gesture (iOS/Chrome autoplay
   *  policy). Must be called synchronously inside a tap/click for sound to work. */
  async unlock() {
    try {
      if (!this.audioContext) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) {
          this.audioContext = new Ctx();
          this.masterGain = this.audioContext.createGain();
          this.masterGain.gain.value = this.effectVolume;
          this.masterGain.connect(this.audioContext.destination);
        }
      }
      if (this.audioContext && this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
    } catch (_) { /* ignore */ }
  }

  /**
   * Where real-sample MP3/OGG files live (relative to /public).
   * Drop CC0/CC-BY loop files here to upgrade free-tier procedural sounds to
   * studio-quality field recordings. The manager will try each path in order
   * and cache the first one that loads successfully.
   */
  static SAMPLE_PATHS = {
    wind: ['/ambient/wind.mp3', '/ambient/wind.ogg'],
    rain: ['/ambient/rain.mp3', '/ambient/rain.ogg'],
    storm: ['/ambient/storm.mp3'],
    fire: ['/ambient/fire.mp3'],
    water: ['/ambient/water.mp3'],
    birds: ['/ambient/birds.mp3'],
    sheep: ['/ambient/sheep.mp3'],
    crowd: ['/ambient/crowd.mp3'],
    night: ['/ambient/night.mp3'],
    morning: ['/ambient/morning.mp3'],
    desert: ['/ambient/desert.mp3'],
    bells: ['/ambient/bells.mp3'],
  };

  async _loadSample(name) {
    if (!this.audioContext) return null;
    if (this.sampleBuffers.has(name)) return this.sampleBuffers.get(name);
    if (this.sampleMissing.has(name)) return null;
    const paths = AudioEffectsManager.SAMPLE_PATHS[name] || [];
    for (const path of paths) {
      try {
        const res = await fetch(path, { cache: 'force-cache' });
        if (!res.ok) continue;
        const arr = await res.arrayBuffer();
        const buf = await this.audioContext.decodeAudioData(arr);
        this.sampleBuffers.set(name, buf);
        return buf;
      } catch (_) {
        // try next
      }
    }
    this.sampleMissing.add(name);
    return null;
  }

  _buildFromSample(buffer) {
    const ctx = this.audioContext;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    gain.connect(this.masterGain);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    src.connect(gain);
    src.start();
    return { sources: [src], gain };
  }

  /* ---------------------------------------------------------------------- */
  /*  Keyword → effect mapping (auto mode)                                   */
  /* ---------------------------------------------------------------------- */
  effectPatterns = {
    wind: {
      keywords: ['wind', 'breeze', 'gust', 'whirlwind', 'tempest', 'blew', 'blowing',
        'viento', 'brisa', 'ráfaga', 'torbellino', 'sopló', 'soplaba', 'soplo'],
      intensity: 0.45,
    },
    rain: {
      keywords: ['rain', 'rainfall', 'shower', 'downpour', 'drizzle', 'rained',
        'lluvia', 'llovió', 'llover', 'aguacero', 'chubasco', 'llovía'],
      intensity: 0.5,
    },
    storm: {
      keywords: ['storm', 'thunder', 'lightning', 'thundered', 'thunderbolt',
        'tormenta', 'tempestad', 'trueno', 'truenos', 'relámpago', 'relampago', 'rayo', 'rayos'],
      intensity: 0.6,
    },
    fire: {
      keywords: ['fire', 'flame', 'flames', 'burning', 'blaze', 'furnace', 'consumed', 'kindled',
        'fuego', 'llama', 'llamas', 'ardiendo', 'ardió', 'hoguera', 'horno', 'consumió', 'quemó'],
      intensity: 0.4,
    },
    water: {
      keywords: [
        'river', 'stream', 'water', 'waters', 'flowing', 'fountain',
        'sea', 'ocean', 'waves', 'brook', 'jordan', 'galilee',
        'río', 'rio', 'arroyo', 'agua', 'aguas', 'fuente', 'mar', 'océano', 'oceano',
        'olas', 'jordán', 'jordan', 'galilea',
      ],
      intensity: 0.45,
    },
    birds: {
      keywords: ['bird', 'dove', 'eagle', 'raven', 'sparrow', 'wings', 'doves',
        'ave', 'aves', 'pájaro', 'pajaro', 'paloma', 'palomas', 'águila', 'aguila', 'cuervo', 'gorrión', 'alas'],
      intensity: 0.35,
    },
    sheep: {
      keywords: ['sheep', 'lamb', 'lambs', 'flock', 'shepherd', 'pasture',
        'oveja', 'ovejas', 'cordero', 'corderos', 'rebaño', 'rebano', 'pastor', 'pastos', 'pradera'],
      intensity: 0.35,
    },
    crowd: {
      keywords: ['crowd', 'multitude', 'assembly', 'congregation', 'people gathered',
        'multitud', 'muchedumbre', 'gentío', 'gentio', 'asamblea', 'congregación', 'congregacion', 'pueblo'],
      intensity: 0.4,
    },
    night: {
      keywords: ['night', 'darkness', 'midnight', 'evening', 'nightfall',
        'noche', 'oscuridad', 'tinieblas', 'medianoche', 'anochecer', 'atardecer'],
      intensity: 0.3,
    },
    morning: {
      keywords: ['morning', 'dawn', 'sunrise', 'daybreak', 'early', 'light', 'sunshine', 'brightness',
        'mañana', 'manana', 'alba', 'amanecer', 'aurora', 'luz', 'resplandor', 'claridad'],
      intensity: 0.35,
    },
    desert: {
      keywords: ['desert', 'wilderness', 'sand', 'dunes', 'arid', 'parched', 'earth', 'world', 'ground',
        'desierto', 'yermo', 'arena', 'dunas', 'árido', 'arido', 'seco', 'tierra', 'suelo', 'polvo'],
      intensity: 0.35,
    },
    bells: {
      keywords: ['temple', 'tabernacle', 'altar', 'sanctuary', 'holy place',
        'templo', 'tabernáculo', 'tabernaculo', 'altar', 'santuario', 'lugar santo'],
      intensity: 0.35,
    },
  };

  /* ---------------------------------------------------------------------- */
  /*  Curated scene presets (manual mode)                                    */
  /* ---------------------------------------------------------------------- */
  scenePresets = {
    wilderness: { label: 'Wilderness', icon: '🏜️', layers: ['wind', 'desert'] },
    seaside: { label: 'Seaside (Galilee)', icon: '🌊', layers: ['water', 'birds'] },
    garden: { label: 'Garden of Eden', icon: '🌿', layers: ['birds', 'water'] },
    temple: { label: 'Temple', icon: '🕍', layers: ['bells', 'crowd'] },
    storm: { label: 'Storm at Sea', icon: '⛈️', layers: ['storm', 'wind', 'water'] },
    night: { label: 'Night Watch', icon: '🌙', layers: ['night', 'wind'] },
    battle: { label: 'Battlefield', icon: '⚔️', layers: ['crowd', 'wind'] },
    mountain: { label: 'Mountaintop', icon: '⛰️', layers: ['wind', 'birds'] },
    fireside: { label: 'Fireside', icon: '🔥', layers: ['fire', 'night'] },
    pasture: { label: 'Green Pasture', icon: '🐑', layers: ['sheep', 'birds', 'water'] },
  };

  getScenePresets() {
    return Object.entries(this.scenePresets).map(([id, s]) => ({
      id,
      label: s.label,
      icon: s.icon,
    }));
  }

  /* ---------------------------------------------------------------------- */
  /*  Noise buffer (pink-ish noise, reused by many effects)                  */
  /* ---------------------------------------------------------------------- */
  _getNoiseBuffer() {
    if (!this.audioContext) return null;
    if (this._noiseBuffer) return this._noiseBuffer;

    const sr = this.audioContext.sampleRate;
    const seconds = 3;
    const buffer = this.audioContext.createBuffer(1, sr * seconds, sr);
    const data = buffer.getChannelData(0);

    // Pink noise via Paul Kellet's approximation (softer than white)
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < data.length; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + white * 0.0555179;
      b1 = 0.99332 * b1 + white * 0.0750759;
      b2 = 0.96900 * b2 + white * 0.1538520;
      b3 = 0.86650 * b3 + white * 0.3104856;
      b4 = 0.55000 * b4 + white * 0.5329522;
      b5 = -0.7616 * b5 - white * 0.0168980;
      data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
      b6 = white * 0.115926;
    }
    this._noiseBuffer = buffer;
    return buffer;
  }

  _makeNoiseSource({ loop = true } = {}) {
    const buf = this._getNoiseBuffer();
    if (!buf) return null;
    const src = this.audioContext.createBufferSource();
    src.buffer = buf;
    src.loop = loop;
    return src;
  }

  /* ---------------------------------------------------------------------- */
  /*  Effect builders — each returns { sources, gain } connected to master   */
  /* ---------------------------------------------------------------------- */
  _buildEffect(name) {
    if (!this.audioContext) return null;
    const ctx = this.audioContext;
    const gain = ctx.createGain();
    gain.gain.value = 0; // fade in externally
    gain.connect(this.masterGain);
    const sources = [];

    const addNoise = (filterType, freq, q, level) => {
      const src = this._makeNoiseSource();
      if (!src) return;
      const filter = ctx.createBiquadFilter();
      filter.type = filterType;
      filter.frequency.value = freq;
      if (q !== undefined) filter.Q.value = q;
      const lvl = ctx.createGain();
      lvl.gain.value = level;
      src.connect(filter);
      filter.connect(lvl);
      lvl.connect(gain);
      src.start();
      sources.push(src);
    };

    const addOsc = (type, freq, level, modulate) => {
      const osc = ctx.createOscillator();
      osc.type = type;
      osc.frequency.value = freq;
      const lvl = ctx.createGain();
      lvl.gain.value = level;
      osc.connect(lvl);
      lvl.connect(gain);
      if (typeof modulate === 'function') modulate(osc, lvl);
      osc.start();
      sources.push(osc);
    };

    switch (name) {
      case 'wind':
        addNoise('lowpass', 500, 0.7, 0.55);
        addNoise('bandpass', 900, 1.2, 0.2);
        // Slow amplitude LFO for gusts
        {
          const lfo = ctx.createOscillator();
          const lfoGain = ctx.createGain();
          lfo.frequency.value = 0.15;
          lfoGain.gain.value = 0.3;
          lfo.connect(lfoGain);
          lfoGain.connect(gain.gain);
          lfo.start();
          sources.push(lfo);
        }
        break;

      case 'rain':
        addNoise('highpass', 1800, 0.5, 0.45);
        addNoise('bandpass', 4000, 0.8, 0.25);
        break;

      case 'storm':
        addNoise('lowpass', 200, 0.7, 0.5);
        addNoise('highpass', 2200, 0.5, 0.3);
        // Occasional thunder rumble via LFO-modulated low-pass noise
        addOsc('sine', 45, 0.18, (osc, lvl) => {
          const lfo = ctx.createOscillator();
          const lfoGain = ctx.createGain();
          lfo.frequency.value = 0.07;
          lfoGain.gain.value = 0.15;
          lfo.connect(lfoGain);
          lfoGain.connect(lvl.gain);
          lfo.start();
          sources.push(lfo);
        });
        break;

      case 'fire':
        addNoise('bandpass', 1500, 2.5, 0.35);
        addNoise('highpass', 4000, 1.5, 0.18);
        // Low crackle tone
        addOsc('sawtooth', 140, 0.05);
        break;

      case 'water':
        addNoise('bandpass', 700, 3, 0.3);
        addOsc('sine', 320, 0.06, (osc) => {
          osc.frequency.setValueAtTime(320, ctx.currentTime);
          // Gentle vibrato
          const lfo = ctx.createOscillator();
          const lfoGain = ctx.createGain();
          lfo.frequency.value = 0.8;
          lfoGain.gain.value = 40;
          lfo.connect(lfoGain);
          lfoGain.connect(osc.frequency);
          lfo.start();
          sources.push(lfo);
        });
        break;

      case 'birds':
        // Chirpy sine sweeps at random intervals
        addOsc('sine', 2000, 0.08, (osc, lvl) => {
          const pattern = () => {
            if (!this.activeEffects.has('birds')) return;
            const now = ctx.currentTime;
            osc.frequency.setValueAtTime(1800 + Math.random() * 1200, now);
            osc.frequency.linearRampToValueAtTime(2400 + Math.random() * 800, now + 0.15);
            lvl.gain.setValueAtTime(0.001, now);
            lvl.gain.linearRampToValueAtTime(0.12, now + 0.05);
            lvl.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
            setTimeout(pattern, 1200 + Math.random() * 2500);
          };
          setTimeout(pattern, 300);
        });
        break;

      case 'sheep':
        // Soft low bleats at slow intervals
        addOsc('sawtooth', 180, 0.0, (osc, lvl) => {
          const bleat = () => {
            if (!this.activeEffects.has('sheep')) return;
            const now = ctx.currentTime;
            osc.frequency.setValueAtTime(170 + Math.random() * 30, now);
            osc.frequency.linearRampToValueAtTime(150, now + 0.4);
            lvl.gain.setValueAtTime(0.0, now);
            lvl.gain.linearRampToValueAtTime(0.18, now + 0.1);
            lvl.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
            setTimeout(bleat, 3500 + Math.random() * 4000);
          };
          setTimeout(bleat, 500);
        });
        break;

      case 'crowd':
        addNoise('bandpass', 600, 1.5, 0.35);
        addNoise('bandpass', 1200, 2, 0.2);
        break;

      case 'night':
        // Cricket-like high chirps over low hum
        addOsc('sine', 90, 0.12);
        addOsc('triangle', 4200, 0.0, (osc, lvl) => {
          const chirp = () => {
            if (!this.activeEffects.has('night')) return;
            const now = ctx.currentTime;
            lvl.gain.setValueAtTime(0.06, now);
            lvl.gain.setValueAtTime(0.001, now + 0.08);
            lvl.gain.setValueAtTime(0.06, now + 0.16);
            lvl.gain.setValueAtTime(0.001, now + 0.24);
            setTimeout(chirp, 400 + Math.random() * 400);
          };
          setTimeout(chirp, 200);
        });
        break;

      case 'morning':
        addOsc('sine', 440, 0.08);
        addOsc('sine', 660, 0.05);
        break;

      case 'desert':
        addNoise('lowpass', 300, 0.5, 0.4);
        break;

      case 'bells':
        // Distant bell-like tones
        addOsc('sine', 523, 0.0, (osc, lvl) => {
          const ring = () => {
            if (!this.activeEffects.has('bells')) return;
            const now = ctx.currentTime;
            const freqs = [523, 659, 784];
            osc.frequency.setValueAtTime(freqs[Math.floor(Math.random() * freqs.length)], now);
            lvl.gain.setValueAtTime(0.0, now);
            lvl.gain.linearRampToValueAtTime(0.14, now + 0.05);
            lvl.gain.exponentialRampToValueAtTime(0.001, now + 2.5);
            setTimeout(ring, 4500 + Math.random() * 4000);
          };
          setTimeout(ring, 500);
        });
        break;

      default:
        // Unknown effect — no-op
        gain.disconnect();
        return null;
    }

    return { sources, gain };
  }

  /* ---------------------------------------------------------------------- */
  /*  Public API                                                             */
  /* ---------------------------------------------------------------------- */
  analyzeText(text) {
    const detected = [];
    const lower = (text || '').toLowerCase();
    for (const [name, cfg] of Object.entries(this.effectPatterns)) {
      if (cfg.keywords.some((k) => lower.includes(k))) {
        detected.push(name);
      }
    }
    return detected;
  }

  /**
   * Incrementally start/stop layers to keep active set ⊆ desired set.
   * Used by live verse-timed crossfading during narration.
   * @param {string[]} desired
   * @param {number} maxSimultaneous
   */
  syncLayers(desired, maxSimultaneous = 3) {
    if (!this.effectsEnabled) return [];
    const target = (desired || []).slice(0, maxSimultaneous);
    const current = new Set(this.activeEffects.keys());
    // Stop anything that's active but no longer desired
    current.forEach((name) => {
      if (!target.includes(name)) this._stopEffect(name, 1200);
    });
    // Start newly desired layers
    target.forEach((name) => {
      if (!current.has(name)) this._startEffect(name, 0.7);
    });
    return target;
  }

  /**
   * Start a named effect with a smooth fade-in. If already active, does nothing.
   * If `useRealSamples` is true and a CC0 sample is available for the layer,
   * the manager streams the real recording instead of synthesising.
   * @param {string} name
   * @param {number} targetLevel 0..1 relative to master effect volume
   */
  _startEffect(name, targetLevel = 0.6) {
    if (!this.audioContext || !this.effectsEnabled) return false;
    if (this.activeEffects.has(name)) return true;

    // Resume context if suspended (Chrome autoplay policy)
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume().catch(() => {});
    }

    // Try to use a real sample first (premium) — else fall back to procedural.
    if (this.useRealSamples && !this.sampleMissing.has(name)) {
      const cached = this.sampleBuffers.get(name);
      if (cached) {
        const built = this._buildFromSample(cached);
        this.activeEffects.set(name, built);
        const now = this.audioContext.currentTime;
        built.gain.gain.setValueAtTime(0, now);
        built.gain.gain.linearRampToValueAtTime(targetLevel, now + 1.2);
        return true;
      }
      // Kick off an async load; on next call we'll use it. Synth in the meantime.
      this._loadSample(name).then((buf) => {
        if (!buf || !this.activeEffects.has(name)) return;
        // Swap: the synth layer is currently active — crossfade to sample.
        const synthLayer = this.activeEffects.get(name);
        const sampleLayer = this._buildFromSample(buf);
        const now = this.audioContext.currentTime;
        sampleLayer.gain.gain.setValueAtTime(0, now);
        sampleLayer.gain.gain.linearRampToValueAtTime(targetLevel, now + 0.8);
        synthLayer.gain.gain.cancelScheduledValues(now);
        synthLayer.gain.gain.setValueAtTime(synthLayer.gain.gain.value, now);
        synthLayer.gain.gain.linearRampToValueAtTime(0, now + 0.8);
        setTimeout(() => {
          synthLayer.sources.forEach((s) => {
            try { s.stop(); } catch (_) { /* may already be stopped */ }
            try { s.disconnect(); } catch (_) { /* may already be disconnected */ }
          });
          try { synthLayer.gain.disconnect(); } catch (_) { /* may already be disconnected */ }
          this.activeEffects.set(name, sampleLayer);
        }, 850);
      });
    }

    const built = this._buildEffect(name);
    if (!built) return false;
    this.activeEffects.set(name, built);

    const now = this.audioContext.currentTime;
    built.gain.gain.cancelScheduledValues(now);
    built.gain.gain.setValueAtTime(0, now);
    built.gain.gain.linearRampToValueAtTime(targetLevel, now + 1.2);
    return true;
  }

  _stopEffect(name, fadeMs = 800) {
    const entry = this.activeEffects.get(name);
    if (!entry || !this.audioContext) return;
    const now = this.audioContext.currentTime;
    try {
      entry.gain.gain.cancelScheduledValues(now);
      entry.gain.gain.setValueAtTime(entry.gain.gain.value, now);
      entry.gain.gain.linearRampToValueAtTime(0, now + fadeMs / 1000);
      setTimeout(() => {
        entry.sources.forEach((s) => {
          try { s.stop(); } catch (_) { /* already stopped */ }
          try { s.disconnect(); } catch (_) { /* ignore */ }
        });
        try { entry.gain.disconnect(); } catch (_) { /* ignore */ }
      }, fadeMs + 50);
    } catch (_) {
      // ignore
    }
    this.activeEffects.delete(name);
  }

  /**
   * Play effects derived from verse text.
   * @param {string} verseText
   * @param {number} duration ms (auto-stop after this; pass 0 to loop)
   * @returns {string[]} active effect names
   */
  playEffectsForVerse(verseText, duration = 15000) {
    this.stopAllEffects();
    if (!this.effectsEnabled) return [];
    const detected = this.analyzeText(verseText);
    // Cap at 3 simultaneous layers for clarity
    const chosen = detected.slice(0, 3);
    chosen.forEach((name) => this._startEffect(name, 0.75));
    if (duration > 0) {
      setTimeout(() => this.stopAllEffects(), duration);
    }
    return chosen;
  }

  /**
   * Play a curated scene preset (manual mode). Loops until stopped.
   * @param {string} sceneId
   * @returns {string[]} active layer names
   */
  playScene(sceneId) {
    this.stopAllEffects();
    if (!this.effectsEnabled) return [];
    const preset = this.scenePresets[sceneId];
    if (!preset) return [];
    preset.layers.forEach((name) => this._startEffect(name, 0.7));
    return preset.layers;
  }

  stopAllEffects() {
    Array.from(this.activeEffects.keys()).forEach((name) => this._stopEffect(name, 500));
  }

  setEffectVolume(volume) {
    this.effectVolume = Math.max(0, Math.min(1, volume));
    if (this.masterGain && this.audioContext) {
      const now = this.audioContext.currentTime;
      this.masterGain.gain.cancelScheduledValues(now);
      this.masterGain.gain.linearRampToValueAtTime(this.effectVolume, now + 0.2);
    }
  }

  setEffectsEnabled(enabled) {
    this.effectsEnabled = enabled;
    if (!enabled) this.stopAllEffects();
  }

  /**
   * Toggle use of real CC0 field-recording samples (premium upgrade).
   * When enabled, the manager will attempt to load MP3/OGG files from
   * `/ambient/<name>.mp3` and crossfade them over the procedural layers.
   */
  setUseRealSamples(enabled) {
    this.useRealSamples = !!enabled;
  }

  /**
   * Best-effort preload — returns the list of layers that have samples available.
   */
  async preloadSamples(names) {
    const results = await Promise.all((names || []).map((n) => this._loadSample(n)));
    return (names || []).filter((_, i) => !!results[i]);
  }

  getActiveEffects() {
    return Array.from(this.activeEffects.keys());
  }
}

export default AudioEffectsManager;
